import { useCallback, useState } from 'react';

import { AxiosRequestConfig } from 'axios';
import { ListOptions } from './infinity-list/infinityList.types';
import { Fetcher, ListManager, ListState } from './useList.types';

const DEFAULT_ITEMS_PER_PAGE = 10;

const getHigienizedParams = (listOptions?: ListOptions) => {
  const defaultParams: Record<string, any> = { ...listOptions };
  delete defaultParams.itemsPerPage;
  return defaultParams;
};

/**
 * @deprecated Use useInfinityList instead.
 */
export const useList = <T, ID>(
  fetcher: Fetcher<T>,
  listOptions?: ListOptions,
): ListManager<T, ID> => {
  const [state, setState] = useState<ListState<T>>({
    tag: 'empty',
    loading: false,
  });
  const [selecteds, setSelecteds] = useState<ID[]>([]);
  const [itemsPerPage] = useState<number>(listOptions?.itemsPerPage ?? DEFAULT_ITEMS_PER_PAGE);

  const [params, setParams] = useState<any>(getHigienizedParams(listOptions));

  const toggleSelected = useCallback(
    (id: ID) => {
      const newSelecteds = selecteds.includes(id)
        ? selecteds.filter((selectedId: ID) => id !== selectedId)
        : selecteds.concat(id);
      setSelecteds(newSelecteds);
    },
    [selecteds, setSelecteds],
  );

  const changeSelecteds = useCallback(
    (newSelecteds: ID[]) => {
      setSelecteds(newSelecteds);
    },
    [selecteds, setSelecteds],
  );

  const fetchItems = useCallback(
    async (page: number, newParams: any, config?: any): Promise<{ data: any[]; count: number }> => {
      const newValidParams = { ...params, ...newParams };
      const response = await fetcher({
        page: page > 0 ? page - 1 : page,
        count: itemsPerPage,
        params: newValidParams,
        config,
      });

      if (response.tag === 'success') {
        setParams(newValidParams);
        return response;
      }
      return { data: [], count: 0 };
    },
    [params, setParams],
  );

  const fetchPage = useCallback(
    async (page: number, newParams?: any, axiosConfig?: AxiosRequestConfig) => {
      const pageToLoad = page || 1;
      setState({ tag: 'empty', loading: true });
      const recoveredItems = await fetchItems(pageToLoad, newParams, axiosConfig);
      setState({
        ...state,
        tag: 'with-data',
        loading: false,
        page: pageToLoad,
        list: recoveredItems?.data || [],
        totalItems: recoveredItems?.count,
        hasMore: recoveredItems?.data?.length < recoveredItems?.count,
      });
    },
    [],
  );

  const reset = () => {
    setParams(getHigienizedParams(listOptions));
    setSelecteds([]);
    setState({ tag: 'empty', loading: false });
  };

  const reload = useCallback(() => fetchPage(1, params), [fetchPage, params]);

  const replaceItems = (list: T[]) => {
    if (state.tag === 'with-data') {
      setState({ ...state, list });
    }
  };

  const fetchMore = useCallback(async () => {
    const nextPage = state.tag !== 'empty' ? state.page + 1 : 1;

    if (state.tag === 'with-data') {
      setState({ ...state, loading: true });
    } else {
      setState({ tag: 'empty', loading: true });
    }

    const currentList = state.tag === 'with-data' ? state.list : [];
    const recoveredItems = await fetchItems(nextPage, params);
    const newList = currentList.concat(recoveredItems.data);

    setState({
      ...state,
      tag: 'with-data',
      loading: false,
      page: nextPage,
      list: newList,
      totalItems: recoveredItems.count,
      hasMore: newList.length < recoveredItems.count,
    });
  }, [fetchItems, params, state]);

  return {
    state,
    params,
    selecteds,
    reset,
    reload,
    toggleSelected,
    changeSelecteds,
    onSearch: fetchPage,
    next: fetchMore,
    replaceItems,
  };
};
